<script setup lang="ts">
/**
 * GNB (1뎁스 + 전체 2뎁스 패널)
 *
 * @params none
 * @return none  (호버/포커스 시 서브패널 오픈, 1뎁스 열 강조 및 2뎁스 링크 이동)
 * @author utis
 * @see    ~/store/layout/gnb, ~/types/layout/gnb
 */
import { computed, ref, onBeforeUnmount } from 'vue'
import { storeToRefs } from 'pinia'
import { useGnbStore } from '~/store/layout/gnb'

/** 메뉴 타입 (store와 동일 구조, 필요한 필드만) */
export type GnbChild = {
  id: number
  label: string
  url: string
}
export type GnbItem = {
  id: number
  label: string
  url: string
  showgnb?: boolean
  children?: GnbChild[]
}

const gnbStore = useGnbStore()
const { firstdepth } = storeToRefs(gnbStore)

/** 보여줄 1뎁스만 필터 */
const menus = computed<GnbItem[]>(() =>
    firstdepth.value.filter((m) => m.showgnb !== false)
)

/** 서브패널 열림/닫힘 + 활성 열 ID */
const isOpen = ref(false)
const activeId = ref<number | null>(null)

/**
 * 서브패널 닫힘 딜레이 타이머
 * @params none
 * @return none
 * @author utis
 * @see    gnb hover intent
 */
const hideTimer = ref<number | null>(null)

/**
 * 닫힘 타이머 취소
 * @params none
 * @return none
 * @author utis
 * @see    cancel close on hover
 */
const cancelClose = () => {
  if (hideTimer.value) {
    clearTimeout(hideTimer.value)
    hideTimer.value = null
  }
}

/**
 * 지연 닫기 (hover intent)
 * @params none
 * @return none   (160ms 후 패널 닫기, 재진입 시 취소됨)
 * @author utis
 * @see    closeWithDelay
 */
const closeWithDelay = () => {
  cancelClose()
  hideTimer.value = window.setTimeout(() => {
    isOpen.value = false
    activeId.value = null
  }, 160) // 120~200ms 권장
}

/** @params id 활성화할 1뎁스 ID */
const openBy = (id: number) => {
  activeId.value = id
  isOpen.value = true
  cancelClose() // 깜빡임 방지
}

/** @return none 즉시 닫기 (키보드 포커스 이탈 등) */
const closePanel = () => {
  isOpen.value = false
  activeId.value = null
  cancelClose()
}

/** 키보드 접근성: 1뎁스 포커스 시 오픈 */
const onFocusRoot = (id: number) => openBy(id)

/** 포커스가 래퍼 바깥으로 나가면 닫기 */
const onBlurWrap = (e: FocusEvent) => {
  const current = e.currentTarget as HTMLElement
  setTimeout(() => {
    if (!current.contains(document.activeElement)) closePanel()
  }, 0)
}

onBeforeUnmount(() => cancelClose())
</script>

<template>
  <div
      class="gnb-wrap"
      :class="{ 'is-open': isOpen }"
      @mouseenter="cancelClose"
      @mouseleave="closeWithDelay"
      @blur="onBlurWrap"
      tabindex="-1"
  >
    <nav class="nav gnb-nav">
      <ul class="gnb-1depth">
        <li
            v-for="item in menus"
            :key="item.id"
            class="gnb-1depth__item"
            @mouseenter="openBy(item.id)"
        >
          <NuxtLink
              :to="item.url"
              class="gnb-1depth__link"
              @focus="onFocusRoot(item.id)"
          >
            {{ item.label }}
          </NuxtLink>
          <div
              v-show="isOpen"
              class="sub-gnb-wrap"
              @mouseenter="cancelClose"
              @mouseleave="closeWithDelay"
          >
            <div
                class="sub-gnb-grid"
                :style="{ gridTemplateColumns: `repeat(${menus.length}, 1fr)` }"
            >
              <div
                  v-for="col in menus"
                  :key="col.id"
                  class="sub-gnb-col"
                  :class="{ 'is-active': activeId === col.id }"
                  @mouseenter="activeId = col.id"
              >
                <ul class="sub-gnb-col__list">
                  <li
                      v-for="c in (col.children || [])"
                      :key="c.id"
                      class="sub-gnb-col__item"
                  >
                    <NuxtLink :to="c.url" class="sub-gnb-col__link">
                      {{ c.label }}
                    </NuxtLink>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </li>
      </ul>
    </nav>


  </div>
</template>

<style scoped>
.gnb-wrap {
  position: relative;

}

/* 1뎁스 */
.gnb-1depth {  }

.gnb-1depth__link {

}
.gnb-1depth__link:hover,
.gnb-1depth__link:focus-visible {
  color: #1a2e89;
  outline: none;
}

/* 풀폭 서브 패널 */
.sub-gnb-wrap {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 100vw;
  max-width: 100vw;
  //width: 100%;
  //max-width: 100%;
  overflow-x: hidden !important;
  top: 49px;
  background: #fff;
  border-top: 1px solid #e5e8ef;
  border-bottom: 1px solid #e5e8ef;

  z-index: 1000;
}

.sub-gnb-grid {
  display: grid;
  gap: 40px;
  align-items: start;
  max-width: 1050px;
  margin: 0 auto;
}

.sub-gnb-col {
  position: relative;
  border-top: 4px solid transparent;
  padding-top: 16px;
  min-height: 160px;
}
.sub-gnb-col.is-active {
  border-top-color: #1a2e89;
}

.sub-gnb-col:not(:first-child)::before {
  content: '';
  position: absolute;
  left: -20px;
  top: 8px;
  bottom: 8px;
  width: 1px;
  background: #e5e8ef;
}

.sub-gnb-col__list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: block;
}
.sub-gnb-col__item + .sub-gnb-col__item {
  margin-top: 16px;
}
.sub-gnb-col__link {
  display: block;
  text-align: center;
  font-size: 18px;
  color: #6b6b6b;
  text-decoration: none;
}
.sub-gnb-col__link:hover,
.sub-gnb-col__link:focus-visible {
  color: #1a2e89;
  text-decoration: underline;
  outline: none;
}
</style>
